import React from "react";

/**
 * PaginationControls - Controles de paginación para GroupedInventoryTable.
 *
 * Replica la misma interfaz de paginación de DataTable:
 * - Indicador "Mostrando X–Y de Z"
 * - Selector de registros por página
 * - Botones Anterior/Siguiente con números de página y ellipsis
 *
 * Props:
 * - limit: number — registros por página
 * - skip: number — registros a saltar (offset)
 * - total: number — total de registros
 * - onPageChange: (skip: number) => void — callback al cambiar de página
 * - onPageSizeChange: (limit: number) => void — callback al cambiar tamaño de página
 * - pageOptions: number[] — opciones de registros por página (default: [50, 100, 200])
 */
export function PaginationControls({
  limit,
  skip,
  total,
  onPageChange,
  onPageSizeChange,
  pageOptions = [50, 100, 200],
}) {
  const totalPages = Math.ceil(total / limit);
  const currentPage = Math.floor(skip / limit);

  const handlePageClick = (page) => {
    if (onPageChange) {
      onPageChange(page * limit);
    }
  };

  if (total <= 0) return null;

  return (
    <div className="flex items-center justify-between mt-4 px-2">
      <div className="flex items-center gap-2 text-sm text-gray-600">
        <span>
          Mostrando {skip + 1}–{Math.min(skip + limit, total)} de {total}
        </span>
        {onPageSizeChange && (
          <select
            value={limit}
            onChange={(e) => onPageSizeChange(Number(e.target.value))}
            className="border border-gray-300 rounded px-2 py-1 text-sm"
          >
            {pageOptions.map((opt) => (
              <option key={opt} value={opt}>
                {opt} por página
              </option>
            ))}
          </select>
        )}
      </div>
      <div className="flex items-center gap-1">
        <button
          disabled={currentPage === 0}
          onClick={() => handlePageClick(currentPage - 1)}
          className="px-3 py-1 text-sm border border-gray-300 rounded hover:bg-gray-100 disabled:opacity-50 disabled:cursor-not-allowed"
        >
          Anterior
        </button>
        {renderPageButtons(currentPage, totalPages, handlePageClick)}
        <button
          disabled={currentPage >= totalPages - 1}
          onClick={() => handlePageClick(currentPage + 1)}
          className="px-3 py-1 text-sm border border-gray-300 rounded hover:bg-gray-100 disabled:opacity-50 disabled:cursor-not-allowed"
        >
          Siguiente
        </button>
      </div>
    </div>
  );
}

/**
 * Renders a compact set of page buttons with ellipsis for large page counts.
 */
function renderPageButtons(currentPage, totalPages, onPageClick) {
  if (totalPages <= 7) {
    return Array.from({ length: totalPages }, (_, i) => (
      <button
        key={i}
        onClick={() => onPageClick(i)}
        className={`px-3 py-1 text-sm border rounded ${
          currentPage === i
            ? "bg-purple text-white border-purple"
            : "border-gray-300 hover:bg-gray-100"
        }`}
      >
        {i + 1}
      </button>
    ));
  }

  const pages = [];
  const addPage = (i) => {
    pages.push(
      <button
        key={i}
        onClick={() => onPageClick(i)}
        className={`px-3 py-1 text-sm border rounded ${
          currentPage === i
            ? "bg-purple text-white border-purple"
            : "border-gray-300 hover:bg-gray-100"
        }`}
      >
        {i + 1}
      </button>
    );
  };

  addPage(0);

  if (currentPage > 2) {
    pages.push(
      <span key="start-ellipsis" className="px-1 text-gray-500">
        …
      </span>
    );
  }

  const start = Math.max(1, currentPage - 1);
  const end = Math.min(totalPages - 2, currentPage + 1);
  for (let i = start; i <= end; i++) {
    addPage(i);
  }

  if (currentPage < totalPages - 3) {
    pages.push(
      <span key="end-ellipsis" className="px-1 text-gray-500">
        …
      </span>
    );
  }

  addPage(totalPages - 1);

  return pages;
}

export default PaginationControls;
